import React, { useEffect, useState } from "react";
import MultiSelectFormContext, { MultiStepFormProps } from "./MultiSelectFormContext";
import { MarketPlaceCreationJob, ExtraFields, UpdateValuesProps } from "./types";
import { FullAddress } from "~/shared/GoogleMapsInput";
import { StringRecord } from "~/types/record";
import yup from "~/utils/yup";
import useAuth from "~/hooks/useAuth";

const hasFieldError = async (validation: any, field: string, value: any): Promise<boolean | string> => {
    try {
        const fieldSchema = yup.reach(validation, field);
        return fieldSchema
            .validate(value)
            .then(() => false)
            .catch((err: Error) => err.message);
    } catch (e) {
        return false;
    }
};

const MultiSelectFormProvider = ({ children }) => {
    const [values, setValues] = useState<Partial<MarketPlaceCreationJob>>({});
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [totalSteps, setTotalSteps] = useState<number>();
    const [errors, setErrors] = useState<StringRecord>();
    const [schema, setSchema] = useState(null);
    const [utmSource, setUtmSource] = useState();
    const [confirmationStep, setConfirmationStep] = useState<number>();
    const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);
    const [isAddressAfter, setIsAddressAfter] = useState<boolean>(false);
    const [isDateAfter, setIsDateAfter] = useState<boolean>(false);

    const { account } = useAuth();

    const handleChange = (
        name: string,
        value: any,
        options: { extra?: boolean; updateFn?: UpdateValuesProps } = {}
    ) => {
        if (options.extra) {
            setValues((f: Partial<MarketPlaceCreationJob>) => {
                const currentValue = f?.extra?.[name] ?? {};

                return {
                    ...f,
                    extra: {
                        ...f.extra,
                        [name]: {
                            ...currentValue,
                            ...value,
                        },
                    },
                };
            });
            return;
        }

        setValues((f: Partial<MarketPlaceCreationJob>) => {
            if (options.updateFn) {
                return options.updateFn(f, value);
            }

            return { ...f, [name]: value };
        });
    };

    const handleBlur = async (name: string, value: any, options: { extra?: boolean } = {}) => {
        if (options.extra) {
            const hasError = await hasFieldError(schema, `extra.${name}.value`, value.value);
            setErrors((e: StringRecord) => ({
                ...e,
                [`extra.${name}.value`]: typeof hasError === "string" ? hasError : "",
            }));
            return;
        }

        const hasError = await hasFieldError(schema, name, value);
        setErrors((e: StringRecord) => ({ ...e, [name]: typeof hasError === "string" ? hasError : "" }));
    };

    const handleStatusUpdate = (fields: Partial<MarketPlaceCreationJob>, value: FullAddress & { region: string }) => {
        return {
            ...fields,
            fullAddress: value.fullAddress,
            street: value.address,
            number: value.number,
            complement: value.complement,
            neighborhood: value.neighborhood,
            zipcode: value.zipCode,
            city: value.cityName,
            state: value.uf,
            region: value.region,
            latitude: value.latitude,
            longitude: value.longitude,
        };
    };

    const updateStepStorage = (component: string, step: number) => {
        localStorage.setItem(component, String(step));
    };

    const clearLocalStorageSteps = (components: string[]) => {
        components.map((component) => localStorage.removeItem(component));
    };

    const updateValuesInLocalStorage = () => {
        localStorage.setItem("values", JSON.stringify(values));
    };

    useEffect(() => {
        const step = Number(localStorage.getItem("confirmation"));

        if (!step) {
            updateValuesInLocalStorage();
        }

        if (step) {
            setConfirmationStep(step);
        }
    }, [values]);

    useEffect(() => {
        if (account?.organization.id) {
            setOpenWarningModal(true);
        }
    }, []);

    return (
        <MultiSelectFormContext.Provider
            value={{
                values,
                setValues,
                currentStep,
                totalSteps,
                setTotalSteps,
                setCurrentStep,
                errors,
                handleBlur,
                handleChange,
                utmSource,
                setUtmSource,
                handleStatusUpdate,
                updateStepStorage,
                clearLocalStorageSteps,
                updateValuesInLocalStorage,
                confirmationStep,
                openWarningModal,
                setOpenWarningModal,
                isAddressAfter,
                isDateAfter,
                setIsDateAfter,
                setIsAddressAfter,
            }}
        >
            {children}
        </MultiSelectFormContext.Provider>
    );
};

export default MultiSelectFormProvider;
export { MultiSelectFormContext };
